import { useEffect, useState, useMemo, useRef, useCallback, useLayoutEffect } from 'react';
import { getChannelDisplayName } from '@/utils/channelLabel';
import { initialsOf } from '@/components/PostComposer/sections/preview/previewIdentity';
import { useTranslation } from 'react-i18next';
import { toast } from '@/utils/toast';
import api, { formatApiError } from '@/services/api';
import { Modal } from '@/components/ui/Modal';
import { Icon } from '@/components/ui/Icon';
import { Tooltip, TooltipProvider } from '@/components/ui/Tooltip';
import { CancelButton } from '@/components/ui/CancelButton';
import { ModalFooter } from '@/components/ui/ModalFooter';
import { ConfirmDialog, ConfirmDialogSimple } from '@/components/ui/ConfirmDialog';
import { PostEditor } from '@/components/composer/PostEditor';
import { MediaSection } from '@/components/PostComposer/sections/MediaSection';
import { LabelsSection } from '@/components/PostComposer/sections/LabelsSection';
import { PlatformsSection } from '@/components/PostComposer/sections/PlatformsSection';
import { ScheduleSection } from '@/components/PostComposer/sections/ScheduleSection';
import { AISection } from '@/components/PostComposer/sections/AISection';
import { HashtagSection } from '@/components/composer/HashtagSection';
import { CustomSelect, type SelectOption } from '@/components/ui/CustomSelect';
import { CreatorToolbar, type CreatorToggle } from './CreatorToolbar';
import { CreatorSectionCard } from './CreatorSectionCard';
import { useResizableColumns } from './useResizableColumns';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useCalendars } from '@/hooks/useCalendar';
import { useCalendarEvents } from '@/hooks/useCalendarEvents';
import { usePostComposer } from '@/hooks/usePostComposer';
import type { RestorableDraft } from '@/hooks/usePostComposer';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { useComposerStore } from '@/stores/composerStore';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import { usePostPermissions, postToReleaseFacts, isInFlightOrCancelledPost } from '@/hooks/usePostPermissions';
import { usePostReleasePermissions } from '@/hooks/usePostReleasePermissions';
import { useReleaseCapabilities } from '@/hooks/useReleaseCapabilities';
import { ReleaseLockBanner, useReleaseLockText } from '@/components/release/ReleaseLockBanner';
import type { ReleaseDenyReason } from '@/types/release';
import { useChannels } from '@/hooks/useChannels';
import { useTimezone } from '@/contexts/TimezoneContext';
import { ICONS } from '@/constants/icons';
import { checkLimits } from '@/constants/platformLimits';
import { checkCaptionWithinLimits, getYoutubeEffectiveCaptionText } from '@/utils/captionLimit';
import { cn } from '@/utils/cn';
import type { MediaItem } from '@/types/posts';
import type { Post } from '@/services/posts';
import type { MediaType } from '@/constants/platformMediaRequirements';
import type { PlatformOptions } from '@/types/scheduled-instances';
import { recordSlowOp } from '@/utils/perfTelemetry';

/** Compare the editable fields with the state loaded for this post, not with an empty draft. */
const postEditSignature = (
  state: ReturnType<typeof useComposerStore.getState>,
  channels?: readonly { id: number; platform: string }[],
): string => JSON.stringify({
  caption: state.caption,
  media: state.media.map((item) => ({
    id: item.id, url: item.url, type: item.type, thumbnail: item.thumbnail,
    width: item.width, height: item.height, size: item.size,
  })),
  selectedChannels: state.selectedChannels,
  scheduledTime: state.scheduledTime?.toISOString() ?? null,
  labels: state.labels,
  calendarId: state.calendarId,
  calendarNoteId: state.calendarNoteId,
  calendarHolidayId: state.calendarHolidayId,
  // The UI fills standard platform defaults after channels load. Those values are not user edits.
  channelOptions: Object.entries(state.channelOptions).filter(([id, value]) => {
    const channel = channels?.find((item) => item.id === Number(id));
    const defaults = channel ? getDefaultPlatformOptions(channel.platform) : null;
    return !defaults || JSON.stringify(value) !== JSON.stringify(defaults);
  }),
  changedThumbnails: [...state.changedThumbnails.entries()],
  // isCarouselMode is not part of the signature: in edit mode it is derived from the media count (media is above).
});

/**
 * PostCreator - Simplified Caption-first Modal Component
 *
 * Major simplifications from original PostCreator:
 * - Single unified hook (usePostComposer) instead of dual-path
 * - No media adapters (shootingAddMedia, shootingRemoveMedia, etc.)
 * - No string-based CTA routing (uses handlePrimaryAction directly)
 * - Cleaner getCtaLabel and getDisabledReason logic
 * - Fixed content locking (only locks APPROVED, not PENDING)
 * - Unified error/loading states
 * - Simplified keyboard shortcuts
 *
 * Features:
 * - Caption-first workflow (Lexical rich text editor)
 * - Icon toolbar with visual toggles
 * - Contextual CTA (Save Draft / Shoot Now / Schedule)
 * - Smooth animations
 * - Dirty state tracking with confirmation
 * - Keyboard shortcuts (Cmd+Enter, Cmd+S, Esc)
 * - Shooting mode (calendar mapping)
 * - AI assistant integration
 * - Platform-specific options
 * - i18n support
 *
 * Design:
 * - Modal with raw mode for custom layout
 * - Brand colors (#F6F8F5 off-white, #D5EEEB mint, black active)
 * - Expanded sections with card styling
 */

const MEDIA_TIP_DISMISSED_KEY = 'agile-assets-media-tip-dismissed';

/**
 * Get default platform options for a given platform
 * Used to initialize channel options when user selects a channel
 * Ensures consistent defaults between frontend display and backend processing
 */
function getDefaultPlatformOptions(platform: string): PlatformOptions | null {
  const normalizedPlatform = platform.toLowerCase();

  switch (normalizedPlatform) {
    case 'instagram':
      return {
        instagram: {
          contentType: 'FEED', // Default to feed, user must opt-in for Reels
          shareReelsFeed: true,
        },
      };
    case 'facebook':
      return {
        facebook: {
          contentType: 'FEED',
        },
      };
    case 'youtube':
      return {
        youtube: {
          privacyStatus: 'public',
          isShort: false,
        },
      };
    case 'tiktok':
      return {
        tiktok: {
          privacyLevel: 'PUBLIC_TO_EVERYONE',
          disableComment: false,
          disableDuet: false,
          disableStitch: false,
        },
      };
    case 'linkedin':
      return {
        linkedin: {
          visibility: 'PUBLIC',
        },
      };
    case 'twitter':
    case 'x':
      return {
        twitter: {
          replySettings: 'everyone',
        },
      };
    case 'threads':
      return {
        threads: {
          replyControl: 'everyone',
        },
      };
    default:
      return null;
  }
}

export interface PostCreatorProps {
  /**
   * Control modal open state
   */
  isOpen: boolean;

  /**
   * Callback when modal should close
   */
  onClose: () => void;

  /**
   * Optional: Post ID for editing existing posts
   */
  editingPostId?: number;

  /**
   * Optional: Pre-fill caption
   */
  initialCaption?: string;

  /**
   * Optional: Pre-fill media
   */
  initialMedia?: MediaItem[];

  /**
   * Optional: Pre-fill channels
   */
  initialChannels?: number[];

  /**
   * Optional: Pre-fill scheduled date
   */
  initialScheduledDate?: Date;

  /**
   * Optional: Pre-fill calendar (for calendar "+" entry point)
   */
  initialCalendarId?: number;

  /**
   * Optional: Pre-fill labels
   */
  initialLabels?: string[];

  /**
   * Optional: Calendar mapping ID for shooting mode
   */
  shootingMappingId?: number;

  /**
   * Optional: In shooting mode, open with the schedule/shoot panel expanded
   * (commit intent). Defaults to true so existing "commit a mapped post" entry
   * points (dashboard) are unchanged. Calendar edit passes false so a mapped,
   * uncommitted post opens as a plain editable "Edit Post / Save Post" view;
   * the user commits explicitly via the rocket toggle.
   */
  autoOpenScheduleOnShoot?: boolean;

  /**
   * Optional: Open with schedule panel expanded
   */
  initialScheduleOpen?: boolean;

  /**
   * Optional: Initial schedule mode ('shoot_now' or 'schedule')
   */
  initialScheduleMode?: 'shoot_now' | 'schedule';

  /**
   * Optional: Callback after successful save
   */
  onSaveSuccess?: (postId?: number) => void;
}

export const PostCreator = ({
  isOpen,
  onClose,
  editingPostId,
  initialCaption,
  initialMedia,
  initialChannels,
  initialScheduledDate,
  initialCalendarId,
  initialLabels,
  shootingMappingId,
  autoOpenScheduleOnShoot = true,
  initialScheduleOpen,
  initialScheduleMode,
  onSaveSuccess,
}: PostCreatorProps) => {
  const __renderStart = performance.now();
  useLayoutEffect(() => {
    recordSlowOp('render:PostCreator', performance.now() - __renderStart);
  });
  const { t, i18n } = useTranslation('common');
  // Tailwind `lg` breakpoint. Responsive sections are mounted for ONE layout only
  // (mobile inline column OR desktop columns) instead of both being CSS-hidden,
  // which previously reconciled the whole channel/preview subtree twice.
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const isEditMode = !!editingPostId;
  const isShootingMode = !!shootingMappingId;

  // State management
  // Determine if this is schedule mode (scheduling existing post to new channels)
  // vs true edit mode (modifying post content)
  const isSchedulingExistingPost = isEditMode && initialScheduleOpen;

  // Unified hook - replaces dual usePostComposer + useShootPost
  // Draft key is scoped by post context (editing an existing post vs a
  // brand-new post) so their local drafts never collide — autosave is
  // currently only active for new posts (see `enableAutoSave` below), but
  // this keeps the key correct if that ever changes.
  const composer = usePostComposer({
    isOpen,
    onTeamChanged: onClose,
    autoSaveKey: editingPostId ? `post-creator-draft_${editingPostId}` : 'post-creator-draft_new',
    enableAutoSave: !isEditMode && !isShootingMode,
    autoSaveDelay: 2000,
    onSuccess: () => {
      onSaveSuccess?.();
      onClose();
    },
    editPostId: editingPostId,
    isScheduleMode: isSchedulingExistingPost,
    shootingMappingId: shootingMappingId,
  });

  // Fetch channels for platform mapping
  const { data: channels } = useChannels();

  // Check client restrictions
  const { canSchedulePost, getRestrictionMessage } = useReviewerRestrictions();

  // Centralized post-action permissions (CB-off workflow rules)
  const permissions = usePostPermissions();

  // Server verdicts of the release rulebook for the post being edited (base post). The client mirror
  // in `permissions` only covers the moments before this loads / when it fails.
  const permsPostId = editingPostId ?? composer.mappingData?.post?.id ?? null;
  const releasePerms = usePostReleasePermissions(permsPostId);
  const serverPerms = releasePerms.base;
  // Create mode (no post id yet): the brand-level verdict of the CURRENT effective team. Fail closed.
  const createCaps = useReleaseCapabilities(isOpen && !isEditMode && permsPostId === null);
  const lockText = useReleaseLockText();

  // Get timezone-aware date formatters (respects user > team > default cascade)
  const { formatDateOnly, formatDateTime } = useTimezone();

  // Fetch real calendars - only when modal is open
  const { data: calendars, isLoading: isLoadingCalendars } = useCalendars({ enabled: isOpen });

  // Local UI state
  const [activeToggles, setActiveToggles] = useState<Set<CreatorToggle>>(new Set());
  // Toggles that render in the right column (excludes hashtags/labels which are in left column)
  const RIGHT_PANEL_TOGGLES: CreatorToggle[] = ['media', 'ai', 'schedule', 'carousel'];
  // Raw toggle state (hook inputs that must run before the permission verdicts exist);
  // rendering uses the derived `hasRightPanelToggles` below, which honors scheduleVisible.
  const rawRightPanelToggles = RIGHT_PANEL_TOGGLES.some(t => activeToggles.has(t));
  const { containerRef: resizeContainerRef, handleProps: resizeHandleProps, leftPercent: resizeLeftPercent } = useResizableColumns(rawRightPanelToggles);
  const [isDirty, setIsDirty] = useState(false);
  // Snapshot of the composer state right after load (compared at check time, so both sides are normalized the same way).
  const mappingStoreId = useComposerStore((state) => state.calendarMappingId);
  const editBaselineRef = useRef<{ postId: number; state: ReturnType<typeof useComposerStore.getState> } | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  // Unsaved-draft restore prompt (shown on a genuine open for a NEW post
  // when a lingering local draft exists — see the effect below).
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const [pendingRestorableDraft, setPendingRestorableDraft] = useState<RestorableDraft | null>(null);
  // Shared "has unsaved content" check - reused by handleClose's discard-confirm
  // gate and by the unsaved-work guard below, so both stay in sync.
  const hasCurrentContent =
    composer.caption.trim().length > 0 ||
    composer.media.length > 0 ||
    composer.selectedChannels.length > 0;
  // Register this editor's dirty state globally while it is open, so the
  // auto-reload mechanism defers to a manual refresh banner instead of
  // silently discarding in-progress work.
  // While the fresh fetch is pending the form is not shown (spinner), so nothing user-made exists yet and the
  // store may still hold the previous session's content: not dirty.
  const hasUnsavedEdit = isEditMode && !composer.isLoadingPost && (
    editBaselineRef.current === null
      ? hasCurrentContent
      : postEditSignature(useComposerStore.getState(), channels) !== postEditSignature(editBaselineRef.current.state, channels)
  );
  useUnsavedGuard(isOpen && (isEditMode ? hasUnsavedEdit : isDirty || hasCurrentContent), 'post-creator');

  useEffect(() => {
    if (!isOpen) {
      editBaselineRef.current = null;
      return;
    }
    // The hook's load effects run first, including when the SAME post reopens.
    const state = useComposerStore.getState();
    const loadedPost = state.originalPost;
    const postLoaded = !!loadedPost && loadedPost.id === editingPostId;
    // Calendar entry of a mapped post (edit + shooting): loadFromMapping REPLACES the loaded post's state (channels,
    // planned date, media...) and clears originalPost, so the baseline must be taken from that final state, not
    // from the earlier loadPost one (which made an untouched mapped post read dirty).
    const mappingLoaded =
      isShootingMode &&
      state.mode === 'commit_mapped' &&
      state.calendarMappingId !== null &&
      state.editingPostId === editingPostId &&
      loadedPost === null;
    // Not while this open's fresh fetch is pending: the store may still hold the previous session's state.
    if (!isEditMode || (!postLoaded && !mappingLoaded) || composer.isLoadingPost || composer.isLoadingMapping) return;
    // The calendar entry point seeds calendarId from the prop (effect below); apply it first so that seed is part of
    // the baseline and does not read as a user edit.
    if (initialCalendarId) useComposerStore.getState().setCalendarId(initialCalendarId);
    // Capture after the hook's load and schedule-mode normalization, on every open.
    editBaselineRef.current = { postId: editingPostId ?? 0, state: useComposerStore.getState() };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode, isShootingMode, editingPostId, composer.originalPost, composer.isLoadingPost, composer.isLoadingMapping, mappingStoreId]);
  const [scheduleMode, setScheduleMode] = useState<'shoot_now' | 'schedule'>('shoot_now');
  const [selectedCalendar, setSelectedCalendar] = useState<string>(
    initialCalendarId ? String(initialCalendarId) : ''
  );
  // Campaign selection - reserved for future campaign dropdown feature
  const [_selectedCampaign, _setSelectedCampaign] = useState<string>('');
  // Event selection state - parsed and passed to calendar mappings
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [showCtaDropdown, setShowCtaDropdown] = useState(false);
  const [isRunningSaveAction, setIsRunningSaveAction] = useState(false);

  // Get stable setter references from store for calendar sync (before useEffects that use them)
  const setCalendarIdInStore = useComposerStore(state => state.setCalendarId);
  const setCalendarEvent = useComposerStore(state => state.setCalendarEvent);
  const updateChannelOptions = useComposerStore(state => state.updateChannelOptions);

  // Sync initialCalendarId to selectedCalendar AND composer store when prop changes
  // This ensures calendar mappings are created when saving from calendar "+" entry point
  useEffect(() => {
    if (initialCalendarId && isOpen) {
      setSelectedCalendar(String(initialCalendarId));
      setCalendarIdInStore(initialCalendarId);
    }
  }, [initialCalendarId, isOpen, setCalendarIdInStore]);

  // Sync initial schedule mode from hook's auto-detection
  useEffect(() => {
    if (isShootingMode && composer.defaultShootMode) {
      setScheduleMode(composer.defaultShootMode);
    }
  }, [isShootingMode, composer.defaultShootMode]);

  // Fetch calendar events for the selected date (after state declaration)
  // Use current date as fallback so events load when calendar is selected but no date picked yet
  const { data: calendarEvents, isLoading: isLoadingEvents } = useCalendarEvents(
    selectedCalendar ? parseInt(selectedCalendar, 10) : null,
    composer.scheduledTime || initialScheduledDate || new Date()
  );

  /**
   * Parse selectedEvent string to extract calendarNoteId or calendarHolidayId
   * Format: "note-123" or "holiday-456"
   */
  const parseSelectedEvent = (event: string): { calendarNoteId?: number; calendarHolidayId?: number } => {
    if (!event) return {};
    const [type, idStr] = event.split('-');
    const id = parseInt(idStr, 10);
    if (isNaN(id)) return {};
    if (type === 'note') return { calendarNoteId: id };
    if (type === 'holiday') return { calendarHolidayId: id };
    return {};
  };

  // Sync selectedEvent to composer store for calendar mapping linkage
  useEffect(() => {
    const parsed = parseSelectedEvent(selectedEvent);
    setCalendarEvent(parsed.calendarNoteId ?? null, parsed.calendarHolidayId ?? null);
  }, [selectedEvent, setCalendarEvent]);

  /**
   * Handle image insertion from AI Briefing Modal (Unsplash)
   * Tracks download with Unsplash API and uploads image to our server
   */
  // Latest busy flag for async callbacks (a render-time closure would be stale by the time an upload resolves).
  const isSavingRef = useRef(false);
  isSavingRef.current = composer.isSaving;

  const handleInsertImage = useCallback(async (imageUrl: string, credit: string, downloadUrl: string) => {
    // Content is frozen while a save is running / unconfirmed (same busy guard as every other edit).
    if (composer.isSaving) return;
    try {
      // 1. Track download with Unsplash (API compliance)
      if (downloadUrl) {
        api.post('/api/unsplash/download', { downloadUrl }).catch((err) => {
          console.warn('Failed to track Unsplash download:', err);
        });
      }

      // 2. Upload image from URL to our server (longer timeout for large images)
      const response = await api.post('/api/media/upload-url', { url: imageUrl }, { timeout: 60000 });
      const uploadedMedia = response.data.data;

      // 3. Create MediaItem from the uploaded media
      const mediaItem: MediaItem = {
        id: uploadedMedia.id.toString(),
        url: uploadedMedia.originalUrl,
        thumbnail: uploadedMedia.thumbnailUrl || uploadedMedia.originalUrl,
        type: 'image',
        filename: uploadedMedia.filename || credit,
        size: uploadedMedia.fileSize || 0,
      };

      // 4. Add to media (no shooting mode adapter needed). A save may have started during the upload.
      if (isSavingRef.current) return;
      composer.addMedia(mediaItem);

      // 5. Append Unsplash photo credit to caption (deduped)
      if (credit) {
        const photographerName = credit.replace(/^(Photo by |ai_briefing_image_photo_by )/i, '');
        const creditLine = `📷 Credit: ${photographerName} on Unsplash`;
        const currentCaption = composer.caption;

        if (!currentCaption.includes(creditLine)) {
          // Check if there are already credit lines at the end
          const hasExistingCredits = currentCaption
            .split('\n')
            .some((line) => line.startsWith('📷 Credit:'));

          const updatedCaption = hasExistingCredits
            ? `${currentCaption}\n${creditLine}`
            : `${currentCaption}${currentCaption.trim() ? '\n\n' : ''}${creditLine}`;

          composer.setCaption(updatedCaption);
        }
      }

      // 6. Open media section so user can see the image
      if (!activeToggles.has('media')) {
        setActiveToggles((prev) => new Set([...prev, 'media']));
      }

      // 7. Show success toast
      toast.success(t('image_added', { defaultValue: 'Image added to post' }));
    } catch (error) {
      console.error('Failed to upload image:', error);
      const errorMessage = formatApiError(error).message;
      toast.error(errorMessage);
    }
  }, [composer, activeToggles, t]);

  // When calendar changes, update composer store
  const handleCalendarChange = (value: string) => {
    setSelectedCalendar(value);
    composer.setCalendarId(value ? parseInt(value, 10) : null);
  };

  // Transform calendars to SelectOption format
  const calendarOptions: SelectOption[] = useMemo(() => {
    if (!calendars) return [];
    return calendars.map((cal) => ({
      value: String(cal.id),
      label: cal.name,
      icon: ICONS.CALENDAR,
      iconColor: cal.color || undefined,
    }));
  }, [calendars]);

  // Transform events to SelectOption format - filtered by selected date
  // Uses same repeat logic as calendar view (see campaignMatcher.ts)
  const selectedDate = composer.scheduledTime || initialScheduledDate || new Date();
  const eventOptions: SelectOption[] = useMemo(() => {
    if (!calendarEvents) return [];

    // Filter events that match the selected date (respecting repeat patterns)
    const filteredEvents = calendarEvents.filter((event) => {
      const eventStart = new Date(event.startDate);
      const eventEnd = event.endDate ? new Date(event.endDate) : null;
      const targetDate = new Date(selectedDate);

      // Normalize to date only (ignore time)
      eventStart.setHours(0, 0, 0, 0);
      if (eventEnd) eventEnd.setHours(0, 0, 0, 0);
      targetDate.setHours(0, 0, 0, 0);

      // Target must be on or after event start
      if (targetDate < eventStart) return false;

      // If there's an end date, target must be on or before it
      if (eventEnd && targetDate > eventEnd) return false;

      // Holidays don't repeat - they're single-day events
      if (event.type === 'holiday') {
        return targetDate.getTime() === eventStart.getTime();
      }

      // Notes: handle repeat types
      const repeatType = event.repeatType;

      // Non-repeating note: check if within range
      if (!repeatType || repeatType === 'NONE') {
        // Single-day (no endAt) - must be same day
        if (!eventEnd) {
          return targetDate.getTime() === eventStart.getTime();
        }
        // Multi-day (has endAt) - already validated within range
        return true;
      }

      // Handle repeat types (same logic as campaignMatcher.ts)
      switch (repeatType) {
        case 'DAILY':
          return true; // Every day within range

        case 'WEEKLY':
          // Same day of week as startDate
          return targetDate.getDay() === eventStart.getDay();

        case 'MONTHLY':
          // Same day of month as startDate
          return targetDate.getDate() === eventStart.getDate();

        case 'YEARLY':
          // Same month and day as startDate
          return (
            targetDate.getMonth() === eventStart.getMonth() &&
            targetDate.getDate() === eventStart.getDate()
          );

        default:
          return false;
      }
    });

    return filteredEvents.map((event) => {
      let description = '';
      let icon: string = ICONS.CALENDAR;

      // For notes - check repeatType to determine if Content Pillar or Campaign
      // Icons match QuickAddMenu (Calendar "+" dropdown)
      if (event.type === 'note') {
        const isRepeating = event.repeatType && event.repeatType !== 'NONE';
        description = isRepeating
          ? t('event_type_content_pillar', { defaultValue: 'Content Pillar' })
          : t('event_type_campaign', { defaultValue: 'Campaign' });
        // Content Pillar = REFRESH (recurring), Campaign = FLAG
        icon = isRepeating ? ICONS.REFRESH : ICONS.FLAG;
      }

      // For holidays - show country code if available
      if (event.type === 'holiday') {
        description = event.countryCode
          ? t('event_type_holiday_country', { defaultValue: 'Holiday · {{country}}', country: event.countryCode })
          : t('event_type_holiday', { defaultValue: 'Holiday' });
        icon = ICONS.GIFT;
      }

      return {
        value: `${event.type}-${event.id}`,
        label: event.title,
        icon,
        description,
      };
    });
  }, [calendarEvents, selectedDate, t]);

  // Media tip state
  const [showMediaTip, setShowMediaTip] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(MEDIA_TIP_DISMISSED_KEY) !== 'true';
    }
    return true;
  });

  const handleDismissMediaTip = () => {
    setShowMediaTip(false);
    localStorage.setItem(MEDIA_TIP_DISMISSED_KEY, 'true');
  };

  // Auto-open ref for labels/hashtags
  const hasAutoOpenedRef = useRef(false);

  // Scroll fade indicator state
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 0);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      setCanScrollUp(false);
      setCanScrollDown(false);
      return;
    }
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      observer.disconnect();
    };
  }, [checkScroll, rawRightPanelToggles, isDesktop]);

  // Re-check scroll after toggle animations complete
  useEffect(() => {
    const timer = setTimeout(checkScroll, 350);
    return () => clearTimeout(timer);
  }, [activeToggles, checkScroll]);

  /**
   * Get selected platforms from selected channel IDs
   */
  const selectedPlatforms = (): string[] => {
    if (composer.selectedChannels.length === 0) {
      return ['linkedin'];
    }

    const activeChannels = channels?.filter((ch) =>
      composer.selectedChannels.includes(ch.id)
    );

    if (!activeChannels || activeChannels.length === 0) {
      return ['linkedin'];
    }

    return [...new Set(activeChannels.map((ch) => ch.platform.toLowerCase()))];
  };

  /**
   * Get channel preview data for PreviewSection
   */
  const channelPreviewData = useMemo(() => {
    if (!channels || composer.selectedChannels.length === 0) return [];
    return channels
      .filter((ch) => composer.selectedChannels.includes(ch.id))
      .map((ch) => ({
        platform: ch.platform.toLowerCase(),
        displayName: getChannelDisplayName(ch),
        initials: initialsOf(getChannelDisplayName(ch)),
        handle: ch.platformUsername || '',
        avatarUrl: (ch.metadata as Record<string, unknown>)?.avatarUrl as string | undefined,
        channelId: ch.id,
        options: composer.channelOptions[ch.id],
      }));
  }, [channels, composer.selectedChannels, composer.channelOptions]);

  /**
   * Check if any media is video type (for platform options validation)
   */
  const hasVideo = useMemo(() => {
    return composer.media.some((m) => m.type === 'video');
  }, [composer.media]);

  /**
   * Extract media types for platform compatibility
   */
  const currentMediaTypesMemo = useMemo((): MediaType[] => {
    if (composer.media.length === 0) {
      return [];
    }
    const types = composer.media.map((item) => item.type.toUpperCase() as MediaType);
    return [...new Set(types)];
  }, [composer.media]);

  /**
   * Initialize form with provided values
   */
  useEffect(() => {
    if (isOpen) {
      if (initialCaption) composer.setCaption(initialCaption);
      if (initialMedia) composer.setMedia(initialMedia);
      if (initialChannels) composer.setChannels(initialChannels);
      if (initialScheduledDate) composer.setScheduledTime(initialScheduledDate);
      if (initialLabels) composer.setLabels(initialLabels);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /**
   * Reset form on a genuine open→closed transition — NOT on initial mount
   * while closed. PostCreator is mounted (with isOpen=false) on many pages
   * so it can be opened on demand; without the `wasOpen` guard this effect
   * fired on every such mount, and 300ms later called `composer.reset()`,
   * writing an EMPTY draft that clobbered any in-progress content (the
   * root cause of drafts vanishing on reload). Resetting form STATE here is
   * fine — the persisted local draft (useAutosave) is untouched by
   * `composer.reset()` and survives for the next open (that's the point).
   */
  const wasOpenForResetRef = useRef(isOpen);
  useEffect(() => {
    const wasOpen = wasOpenForResetRef.current;
    wasOpenForResetRef.current = isOpen;

    if (wasOpen && !isOpen) {
      const timer = setTimeout(() => {
        composer.reset();
        composer.resetSaveUiState();
        setActiveToggles(new Set());
        setIsDirty(false);
        setScheduleMode('shoot_now');
        setSelectedCalendar('');
        _setSelectedCampaign('');
        setSelectedEvent('');
        hasAutoOpenedRef.current = false;
        setShowRestorePrompt(false);
        setPendingRestorableDraft(null);
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /**
   * On a genuine open transition (closed→open) for a brand-new, UN-seeded
   * post — never when editing an existing post, shooting a mapping, or when
   * this open was pre-seeded with content via props (e.g. MediaLibrary's
   * `initialCaption`/`initialMedia`, or a Review & Remix / trend flow) —
   * check for a lingering local OR server-side draft and offer to restore
   * the newer of the two instead of silently repopulating the form. A
   * seeded open always wins over a stale, unrelated draft: showing the
   * prompt there risks the user clicking "Restore" and clobbering the
   * caption/media that was just intentionally seeded.
   *
   * `getRestorableDraft()` is now async (it also fetches the server-side
   * mirror for cross-device recovery), so this effect fetches on open, then
   * decides — guarded with a `cancelled` flag so a fetch that resolves
   * after the modal was closed again (or unmounted) never sets state on a
   * stale render. The prompt is shown only once the decision is made, never
   * speculatively.
   */
  const wasOpenForRestorePromptRef = useRef(false);
  useEffect(() => {
    const wasOpen = wasOpenForRestorePromptRef.current;
    wasOpenForRestorePromptRef.current = isOpen;

    const hasSeedContent =
      !!initialCaption ||
      !!(initialMedia && initialMedia.length > 0) ||
      !!(initialChannels && initialChannels.length > 0) ||
      !!initialScheduledDate ||
      !!(initialLabels && initialLabels.length > 0);

    if (!(isOpen && !wasOpen && !isEditMode && !isShootingMode && !hasSeedContent)) {
      return undefined;
    }

    let cancelled = false;

    void composer.getRestorableDraft().then((restorable) => {
      if (cancelled) return; // Modal was closed/unmounted before the fetch resolved.
      if (restorable) {
        setPendingRestorableDraft(restorable);
        setShowRestorePrompt(true);
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode, isShootingMode, initialCaption, initialMedia, initialChannels, initialScheduledDate, initialLabels]);

  /**
   * User chose "Restore" on the draft-recovery prompt: populate the store
   * from the saved draft. The Calendar/Event dropdowns are driven by
   * separate local state (`selectedCalendar`/`selectedEvent`), not read
   * directly from the store, so they need to be synced explicitly here to
   * match what was just restored — otherwise they'd render blank (or a
   * restored event would be invisible/unchangeable, since the Event select
   * only renders when `selectedCalendar` is truthy).
   */
  const handleRestoreDraft = () => {
    try {
      if (pendingRestorableDraft) {
        const { draft } = pendingRestorableDraft;
        composer.restoreDraft(draft);
        setSelectedCalendar(draft.calendarId ? String(draft.calendarId) : '');
        setSelectedEvent(
          draft.calendarNoteId
            ? `note-${draft.calendarNoteId}`
            : draft.calendarHolidayId
              ? `holiday-${draft.calendarHolidayId}`
              : ''
        );
      }
    } finally {
      // Always close the prompt, even if restoring threw.
      setShowRestorePrompt(false);
      setPendingRestorableDraft(null);
    }
  };

  /**
   * User chose "Start Fresh": discard the offered local draft. The live
   * form is untouched (it's already empty/prop-initialized at this point —
   * we never silently repopulated it), so there's nothing else to reset.
   */
  const handleStartFreshDraft = () => {
    composer.clearDraft();
    setShowRestorePrompt(false);
    setPendingRestorableDraft(null);
  };

  /**
   * Initialize schedule panel when opened with initialScheduleOpen prop
   */
  useEffect(() => {
    if (isOpen && initialScheduleOpen) {
      setActiveToggles((prev) => new Set([...prev, 'schedule']));
      if (initialScheduleMode) {
        setScheduleMode(initialScheduleMode);
      }
    }
    // Only run when modal opens with these props
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialScheduleOpen, initialScheduleMode]);

  /**
   * Track dirty state
   */
  useEffect(() => {
    const hasContent =
      composer.caption.trim().length > 0 ||
      composer.media.length > 0 ||
      composer.selectedChannels.length > 0;

    if (hasContent && isOpen && !isEditMode) {
      setIsDirty(true);
    }
  }, [composer.caption, composer.media.length, composer.selectedChannels.length, isOpen, isEditMode]);

  /**
   * Auto-open Labels, Hashtags, and Media toggles when post has existing values
   */
  useEffect(() => {
    if (hasAutoOpenedRef.current) return;

    const hasLabels = composer.labels.length > 0;
    const hasHashtags = /#\w+/.test(composer.caption);
    const hasMedia = composer.media.length > 0;

    if (hasLabels || hasHashtags || hasMedia) {
      hasAutoOpenedRef.current = true;
      setActiveToggles((prev) => {
        const newSet = new Set(prev);
        if (hasLabels) newSet.add('labels');
        if (hasHashtags) newSet.add('hashtags');
        if (hasMedia) newSet.add('media');
        return newSet;
      });
    }
  }, [composer.labels, composer.caption, composer.media.length]);

  /**
   * Auto-enable carousel mode in edit/shooting mode when multiple media present.
   * In these modes, multiple media always means carousel (no split into separate posts).
   */
  useEffect(() => {
    if (!isOpen) return;
    if (isShootingMode || isEditMode) {
      if (composer.media.length > 1) {
        // Multiple media → auto-enable carousel
        composer.setCarouselMode(true);
      } else {
        // 0 or 1 media → auto-disable carousel (no longer a carousel)
        composer.setCarouselMode(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isShootingMode, isEditMode, composer.media.length]);

  /**
   * Initialize platform options with sensible defaults when channels are selected.
   * This ensures the backend receives explicit values instead of relying on backend defaults.
   * Only initializes options for channels that don't already have options set.
   */
  useEffect(() => {
    if (!channels || !isOpen) return;

    const selectedIds = composer.selectedChannels;
    const currentOptions = composer.channelOptions;

    // Find channels that need default options initialized
    for (const channelId of selectedIds) {
      // Skip if options already exist for this channel
      if (currentOptions[channelId]) continue;

      const channel = channels.find(c => c.id === channelId);
      if (!channel) continue;

      const defaults = getDefaultPlatformOptions(channel.platform);
      if (defaults) {
        // Initialize this channel's options with platform defaults
        updateChannelOptions(channelId, defaults);
      }
    }
  }, [channels, composer.selectedChannels, composer.channelOptions, isOpen, updateChannelOptions]);

  /**
   * Handle toolbar toggle
   */
  const handleToggle = (id: CreatorToggle) => {
    const isOpening = !activeToggles.has(id);

    setActiveToggles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });

    // Sync carousel toggle with composer store
    if (id === 'carousel') {
      composer.setCarouselModeByUser(isOpening);
    }

    // Scroll to the opened section after render
    if (isOpening) {
      requestAnimationFrame(() => {
        // Try mobile section first, then desktop, then left-column variant
        const el =
          document.getElementById(`creator-section-${id}-mobile`) ||
          document.getElementById(`creator-section-${id}`) ||
          document.getElementById(`creator-section-${id}-left`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  /**
   * Handle close with dirty state check
   */
  const handleClose = () => {
    const editChanged = isEditMode && !composer.isLoadingPost && (
      editBaselineRef.current === null
        ? hasCurrentContent
        : postEditSignature(useComposerStore.getState(), channels) !== postEditSignature(editBaselineRef.current.state, channels)
    );
    if (isEditMode ? editChanged : isDirty || hasCurrentContent) {
      setShowDiscardConfirm(true);
      return;
    }

    onClose();
  };

  /**
   * Confirm discard
   */
  const handleConfirmDiscard = () => {
    composer.handleDiscard();
    setShowDiscardConfirm(false);
    onClose();
  };

  /**
   * Get CTA label based on current state
   */
  const getCtaLabel = (): { label: string; icon: string; materialIcon: string; variant: 'primary' | 'danger'; showDropdown: boolean } => {
    const rocketOn = isActivelyShooting || scheduleVisible;
    const hasMappingContext = isShootingMode || !!selectedCalendar;

    // Rocket ON: show shoot/schedule CTA
    if (rocketOn) {
      const isShootNow = scheduleMode === 'shoot_now';
      return {
        label: isShootNow ? t('shoot_now', { defaultValue: 'Shoot Now' }) : t('schedule_post', { defaultValue: 'Schedule Post' }),
        icon: isShootNow ? ICONS.PAPER_PLANE : ICONS.ALARM_CLOCK,
        materialIcon: isShootNow ? 'rocket_launch' : 'schedule',
        variant: 'primary',
        // Schedule panel open but this item can't be scheduled (unreleased / changes requested): only the
        // review / release items are offered, the schedule CTA itself stays disabled with its reason.
        showDropdown: hasReleaseMenu,
      };
    }

    // Already scheduled: content-only save, no shoot
    if (isScheduledMappingEdit) {
      return { label: t('save_changes', { defaultValue: 'Save Changes' }), icon: ICONS.SAVE, materialIcon: 'check_circle', variant: 'primary', showDropdown: false };
    }

    // Check if post is READY
    const isReady = composer.existingPost?.contentStatus === 'READY';

    // READY post - simple save, no dropdown
    if (isReady) {
      return { label: t('save_post', { defaultValue: 'Save Post' }), icon: ICONS.SAVE, materialIcon: 'check_circle', variant: 'primary', showDropdown: hasReleaseMenu };
    }

    // Default: save with dropdown for "Save & Mark Ready"
    // Hide dropdown when workflow is enabled (Mark Ready is replaced by approval flow)
    return {
      label: hasMappingContext
        ? t('save_post', { defaultValue: 'Save Post' })
        : t('save_draft', { defaultValue: 'Save Draft' }),
      icon: ICONS.SAVE,
      materialIcon: 'check_circle',
      variant: 'primary',
      showDropdown: !permissions.workflowEnabled || hasReleaseMenu,
    };
  };

  // True when the last disabled reason is one the user fixes in the channel / date /
  // calendar / limit sections (NOT the release gate or the content lock).
  const setupReasonRef = useRef(false);
  const markSetupReason = (message: string): string => {
    setupReasonRef.current = true;
    return message;
  };

  /**
   * Get disabled reason for CTA
   */
  const getDisabledReason = (): string | null => {
    setupReasonRef.current = false;
    const rocketOn = isActivelyShooting || scheduleVisible;
    const hasContent = composer.caption.trim() || composer.media.length > 0;

    // Frozen content (in review / released / scheduled / not yours): nothing may be saved. Only the
    // rocket (schedule) path can still be open, and it re-sends the stored content unchanged.
    if (isPublishingNowMapping) {
      return t('scheduled_publishing_now_note', { defaultValue: "Publishing now · changes can't be applied" });
    }
    if (isContentLocked && !rocketOn) {
      return lockText(lockReason);
    }

    // No content = always disabled
    if (!hasContent) {
      return t('add_caption_or_media', { defaultValue: 'Add caption or media' });
    }

    // Shooting mode: until the mapping's occurrences are known we cannot tell a plain edit from a (re)shoot,
    // so a fast click can never fire a shoot at an already scheduled post.
    if (isShootingMode && !isContentLocked && composer.isLoadingScheduledInstance) {
      return t('loading_mapped_date', { defaultValue: 'Loading planned date...' });
    }

    // Rocket off = save always works with content
    if (!rocketOn) return null;

    // Hint appended to rocket-on disabled reasons (not in shooting mode where rocket can't be toggled off)
    const saveHint = isShootingMode || isContentLocked
      ? ''
      : ` · ${t('toggle_rocket_to_save', { defaultValue: 'Toggle the rocket off to just save' })}`;

    // Rocket on: need channels
    if (composer.selectedChannels.length === 0) {
      return markSetupReason(t('select_channel_to_publish', { defaultValue: 'Select at least one channel' }) + saveHint);
    }

    // Rocket on: caption must be within platform character limits.
    // YouTube validates its per-channel description override (if set), not the
    // caption — same source of truth as the submit-handler re-validation in
    // usePostComposer.handlePrimaryAction (checkCaptionWithinLimits).
    if (composer.caption.trim()) {
      const platforms = selectedPlatforms();
      const youtubeText = getYoutubeEffectiveCaptionText(
        composer.caption,
        composer.selectedChannels,
        composer.channelOptions,
        channels
      );
      const captionCheck = checkCaptionWithinLimits(composer.caption, platforms, { youtube: youtubeText });
      if (!captionCheck.isWithinAll) {
        return markSetupReason(t('caption_over_limit', { defaultValue: 'Caption too long for: {{platforms}}', platforms: captionCheck.overPlatformNames }) + saveHint);
      }
    }

    // Rocket on: image count must be within platform carousel limits
    const imageCount = composer.media.filter((m) => m.type.toLowerCase() === 'image').length;
    if (imageCount > 0) {
      const platforms = selectedPlatforms();
      const imgCheck = checkLimits(imageCount, platforms, 'carouselImages');
      if (!imgCheck.isWithinAll) {
        const overInfo = imgCheck.overLimits
          .map((o) => `${o.displayName} (max ${o.limit})`)
          .join(', ');
        return markSetupReason(t('images_over_limit', { defaultValue: 'Too many images for: {{platforms}}', platforms: overInfo }) + saveHint);
      }
    }

    // Rocket on: Pinterest channels must have a board selected
    if (channels) {
      const pinterestChannelsWithoutBoard = composer.selectedChannels.filter((chId) => {
        const ch = channels.find((c) => c.id === chId);
        if (!ch || ch.platform.toLowerCase() !== 'pinterest') return false;
        const boardId = composer.channelOptions[chId]?.pinterest?.boardId;
        return !boardId || boardId === '';
      });
      if (pinterestChannelsWithoutBoard.length > 0) {
        return markSetupReason(t('select_pinterest_board', { defaultValue: 'Select a Pinterest board before scheduling' }) + saveHint);
      }
    }

    // Rocket on + non-mapped posts: need calendar
    if (!isShootingMode && !composer.calendarId) {
      return markSetupReason(t('select_calendar_to_publish', { defaultValue: 'Select a calendar' }) + saveHint);
    }

    // Check approval workflow restrictions.
    // For existing posts: use the per-post hook which applies god mode + client rules.
    // For new posts: fall back to the broad canSchedulePost flag (no workflowStatus yet).
    if (composer.existingPost || serverPerms) {
      if (!canScheduleExisting) {
        const denied = serverPerms?.reasons.schedule;
        const message = denied
          ? i18n.t(`errors:release.${denied}`, { keySeparator: false })
          : getRestrictionMessage('schedule post');
        return message + saveHint;
      }
    } else if (!canScheduleHere) {
      return getRestrictionMessage('schedule post') + saveHint;
    }

    // Shooting mode: the schedule date must come from the mapping. Until the mapping GET has
    // settled, scheduledTime is null or only a seed value, so block the CTA (and the Cmd+Enter
    // shortcut, which uses this same check) so a fast click can never submit a placeholder date.
    if (isShootingMode && scheduleMode === 'schedule' && !composer.loadMappingError && (composer.isLoadingMapping || !composer.mappingData)) {
      return t('loading_mapped_date', { defaultValue: 'Loading planned date...' });
    }

    // Schedule mode: need time
    if (scheduleMode === 'schedule' && !composer.scheduledTime) {
      return markSetupReason(t('pick_datetime_to_schedule', { defaultValue: 'Pick a date and time' }) + saveHint);
    }

    // Schedule mode: time must be future
    if (scheduleMode === 'schedule' && composer.scheduledTime && composer.scheduledTime <= new Date()) {
      return markSetupReason(t('schedule_time_must_be_future', { defaultValue: 'Schedule time must be in the future' }) + saveHint);
    }

    return null;
  };

  /**
   * Handle primary action - delegates to unified hook
   */
  const handlePrimaryAction = () => {
    const rocketOn = isActivelyShooting || scheduleVisible;
    composer.handlePrimaryAction({
      rocketOn,
      shootMode: scheduleMode,
      channelIds: composer.selectedChannels,
      scheduledTime: composer.scheduledTime,
      channelOptions: composer.channelOptions,
    });
  };

  /**
   * Keyboard shortcuts
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + Enter to execute primary action
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        const disabledReason = getDisabledReason();
        if (!disabledReason && !isBusy && !releasePerms.isLoading) {
          handlePrimaryAction();
        }
      }

      // Cmd/Ctrl + S to save draft (not in shooting mode)
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        // Same busy guard as the buttons and Cmd/Ctrl+Enter: never start a second save while one runs.
        if (!isShootingMode && composer.canSaveDraft && !isContentLocked && !releasePerms.isLoading && !isBusy) {
          composer.handleSaveDraft();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeToggles, scheduleMode, composer]);

  const isBusy = composer.isSaving || composer.isShooting || isRunningSaveAction;

  // Content is locked when the workflow rules prevent the current user from editing.
  // usePostPermissions centralizes all CB-off workflow restrictions (god mode + client).
  // Server verdict first (exact item, exact rulebook); the client mirror covers loading / failure.
  const existingPostForPerms = composer.existingPost as Post | undefined;
  const clientFacts = existingPostForPerms ? postToReleaseFacts(existingPostForPerms) : null;
  const inFlight = existingPostForPerms ? isInFlightOrCancelledPost(existingPostForPerms) : false;
  const canEditContent: boolean = inFlight
    ? false
    : serverPerms
    ? serverPerms.canEdit
    : clientFacts
      ? permissions.canEditItem(clientFacts)
      : true;
  // The worker has claimed this mapping's occurrence (PICKED_UP): publishing now, nothing can be applied. Read-only.
  const isPublishingNowMapping: boolean =
    isShootingMode && canEditContent && composer.liveScheduledInstance?.status === 'PICKED_UP';
  const isContentLocked = !canEditContent || isPublishingNowMapping;
  const lockReason: ReleaseDenyReason | null = isContentLocked
    ? inFlight || isPublishingNowMapping
      ? 'frozen_committed'
      : serverPerms?.reasons.edit ?? (clientFacts ? permissions.editDenyReasonItem(clientFacts) : null) ?? 'not_creator'
    : null;
  // The opened mapping already has a live scheduled occurrence (QUEUED / picked up, not publishing yet) and the
  // content is editable: plain content edit. The worker reads caption/media fresh at publish time, so a content
  // save is enough; a re-shoot would only hit the backend's 409 publish_in_progress. Derived (never mutates the
  // schedule toggle state). Locked content (released / in review) and planned-only (MAPPED) posts are unaffected.
  const isScheduledMappingEdit: boolean = isShootingMode && !isContentLocked && !!composer.liveScheduledInstance;
  // In shooting mode, user can toggle schedule off to just save/edit
  const isActivelyShooting = isShootingMode && activeToggles.has('schedule') && !isScheduledMappingEdit && !isPublishingNowMapping;
  // Scheduling verdict of the post being edited (null: new post, the broad role flag decides)
  const canScheduleExisting: boolean = serverPerms
    ? serverPerms.canSchedule
    : clientFacts
      ? permissions.canScheduleItem(clientFacts)
      : permissions.workflowEnabled
        ? false // new post, release workflow on: nothing can be scheduled before it is released
        : canSchedulePost;
  // ONE verdict for every schedule / publish-now / date-time control of this composer.
  // Workflow on: only the item's own verdict (server SSOT, mirror while loading); a new post has none.
  // Workflow off: unchanged (role flag, or the existing item's verdict).
  const canScheduleHere: boolean = permissions.workflowEnabled
    ? canScheduleExisting
    : canSchedulePost || (!!(composer.existingPost || serverPerms) && canScheduleExisting);
  // Schedule panel is shown only while its toggle is on AND scheduling is allowed (derived, never
  // enforced by mutating the toggle state). Shooting mode keeps its own flow.
  const scheduleVisible: boolean = !isScheduledMappingEdit && !isPublishingNowMapping && activeToggles.has('schedule') && (!permissions.workflowEnabled || canScheduleHere || isShootingMode);
  // Right column exists only when a toggle that renders there is actually visible (a denied schedule toggle must not leave an empty column + divider)
  const hasRightPanelToggles: boolean = RIGHT_PANEL_TOGGLES.some(t => (t === 'schedule' ? scheduleVisible : activeToggles.has(t)));
  // Now/Later pill: workflow off keeps the old role flag so it behaves exactly as before
  const showNowLaterPill: boolean = permissions.workflowEnabled ? canScheduleHere : canSchedulePost;
  // Opened straight into the schedule panel (Shoot now) but the verdict denies it: say why, once per open
  const scheduleDeniedToastShownRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      scheduleDeniedToastShownRef.current = false;
      return;
    }
    if (!initialScheduleOpen || isShootingMode || !permissions.workflowEnabled || releasePerms.isLoading || releasePerms.isFetching) return;
    if (canScheduleHere || scheduleDeniedToastShownRef.current) return;
    scheduleDeniedToastShownRef.current = true;
    const reason = serverPerms?.reasons.schedule ?? 'not_released';
    toast.info(i18n.t(`errors:release.${reason}`, { keySeparator: false }));
  }, [isOpen, initialScheduleOpen, isShootingMode, permissions.workflowEnabled, releasePerms.isLoading, releasePerms.isFetching, canScheduleHere, serverPerms, i18n]);
  // Save-button dropdown, release workflow ON (carte blanche OFF) and rocket/schedule mode OFF.
  // The source of the verdict: an existing post asks its own release-permissions (server SSOT), a new
  // post asks the brand capabilities. While loading, on error, or on an older server: nothing is shown.
  const isRocketMode: boolean = isActivelyShooting || scheduleVisible;
  const releaseMenuVerdictReady: boolean =
    permissions.workflowEnabled &&
    !(isRocketMode && canScheduleHere) &&
    !isContentLocked &&
    (permsPostId !== null ? !releasePerms.isLoading : !createCaps.isLoading);
  const verdictCanReleaseDirect: boolean =
    permsPostId !== null ? serverPerms?.canReleaseDirect === true : createCaps.canReleaseDirect;
  const verdictCanSendForReview: boolean =
    permsPostId !== null ? serverPerms?.canSendForReview === true : createCaps.canSendForReview;
  const showReleaseDirect: boolean = releaseMenuVerdictReady && verdictCanReleaseDirect;
  const showSaveAndSendForReview: boolean = releaseMenuVerdictReady && verdictCanSendForReview;
  const hasReleaseMenu: boolean = showReleaseDirect || showSaveAndSendForReview;
  // Rocket CTA with only the release menu behind it (schedule not possible here): the toggle must not follow the CTA's disabled reason
  const menuOnly: boolean = isRocketMode && hasReleaseMenu;

  /**
   * Auto-expand the schedule section when committing a mapped post (shooting mode).
   * Skipped when the caller opts out (autoOpenScheduleOnShoot=false, calendar edit):
   * the post then opens as an editable "Edit Post / Save Post" view and the rocket
   * toggle commits explicitly. Content-locked posts (e.g. APPROVED, only schedulable)
   * still auto-expand, since there is nothing to edit/save.
   * NOTE: NOT auto-expanded when creating NEW posts from calendar quick add.
   */
  useEffect(() => {
    if (isShootingMode && isOpen && (autoOpenScheduleOnShoot || (isContentLocked && canScheduleExisting))) {
      setActiveToggles(prev => new Set([...prev, 'schedule']));
    }
  }, [isShootingMode, isOpen, autoOpenScheduleOnShoot, isContentLocked, canScheduleExisting]);

  // Calculate which toolbar toggles should be hidden (not just disabled)
  const hiddenToggles = useMemo(() => {
    const hidden = new Set<CreatorToggle>();

    // Hide schedule toggle if:
    // - Cannot schedule post (normal restriction), AND
    // - The post-permissions hook also disallows scheduling this specific post.
    if (!canScheduleHere) {
      hidden.add('schedule');
    }

    // Plain edit of an already scheduled mapping: rescheduling goes through the calendar, not this composer.
    if (isScheduledMappingEdit || isPublishingNowMapping) {
      hidden.add('schedule');
    }

    // Hide carousel toggle in edit/shooting mode (always auto-carousel when multiple media)
    if (isEditMode || isShootingMode) {
      hidden.add('carousel');
    }

    return hidden;
  }, [canScheduleHere, isEditMode, isShootingMode, isScheduledMappingEdit, isPublishingNowMapping]);

  /** Run a save-then-act handler with the footer marked busy for the whole save + follow-up call. */
  const runSaveAction = async (handler: () => Promise<void>): Promise<void> => {
    setIsRunningSaveAction(true);
    try {
      await handler();
    } finally {
      setIsRunningSaveAction(false);
    }
  };

  const ctaConfig = getCtaLabel();
  const disabledReason = getDisabledReason();
  const disabledReasonIsSetup = setupReasonRef.current;
  // Keep Save disabled until the server verdict arrives (no editable flash on a frozen post)
  const isDisabled = !!disabledReason || isBusy || releasePerms.isLoading;

  return (
    <>
      <TooltipProvider>
        <Modal
          open={isOpen}
          onOpenChange={handleClose}
          size={hasRightPanelToggles ? '2xl' : 'xl'}
          raw={true}
          preventClose={isBusy}
          className="!rounded-md transition-all duration-300"
        >
          {/* Custom Header */}
          <div className="flex-shrink-0 flex items-center justify-between px-6 sm:px-[33px] pt-3 pb-3">
            <h2 className="text-lg sm:text-xl md:text-2xl font-wagner text-gray-900 flex items-center justify-center lg:justify-start gap-2 w-full">
              {(isShootingMode || isSchedulingExistingPost) && (isActivelyShooting || scheduleVisible)
                ? scheduleMode === 'schedule'
                  ? t('schedule_post_title', { defaultValue: 'Schedule Post' })
                  : t('shooting_mode_title', { defaultValue: 'Shoot Post' })
                : isEditMode || isShootingMode
                  ? t('edit_post', { defaultValue: 'Edit Post' })
                  : initialCalendarId
                    ? t('create_and_map_post', { defaultValue: 'Create & Map Post' })
                    : t('create_post', { defaultValue: 'Create Post' })}
              {/* Show date when creating from calendar */}
              {!isShootingMode && !isEditMode && initialCalendarId && initialScheduledDate && (
                <span className="text-base font-normal text-gray-500">
                  · {formatDateOnly(initialScheduledDate)}
                </span>
              )}
            </h2>
          </div>

          {isPublishingNowMapping && (
            <p className="flex-shrink-0 px-6 sm:px-[33px] pb-2 text-xs text-gray-600" role="status">
              {t('scheduled_publishing_now_note', { defaultValue: "Publishing now · changes can't be applied" })}
            </p>
          )}
          {isScheduledMappingEdit && composer.liveScheduledInstance && (
            <p className="flex-shrink-0 px-6 sm:px-[33px] pb-2 text-xs text-gray-600" role="status">
              {t('scheduled_edit_note', {
                defaultValue: 'Scheduled for {{when}} · changes apply to the scheduled post',
                when: formatDateTime(composer.liveScheduledInstance.scheduledAt),
              })}
            </p>
          )}

          {/* Loading / Error States */}
          {composer.isLoadingMapping ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center space-y-4">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" />
                <p className="text-sm text-gray-500">{t('loading_mapped_post', { defaultValue: 'Loading mapped post...' })}</p>
              </div>
            </div>
          ) : composer.loadMappingError ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center space-y-4 max-w-md">
                <div className="text-red-500 text-4xl">
                  <Icon name={ICONS.CAUTION} size="lg" color="text-red-500" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-gray-900">{t('failed_to_load_mapping', { defaultValue: 'Failed to load mapping' })}</h3>
                  <p className="text-sm text-gray-600">
                    {composer.loadMappingError instanceof Error
                      ? composer.loadMappingError.message
                      : t('error_loading_mapping', { defaultValue: 'Error loading mapping data' })}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {t('close', { defaultValue: 'Close' })}
                </button>
              </div>
            </div>
          ) : isEditMode && composer.isLoadingPost ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center space-y-4">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" />
                <p className="text-sm text-gray-500">{t('loading_post', { defaultValue: 'Loading post...' })}</p>
                {/* A way out while the fresh fetch is pending (loading = not dirty, so this closes directly) */}
                <div className="flex justify-center">
                  <CancelButton onClick={handleClose} tooltip={t('cancel', { defaultValue: 'Cancel' })} />
                </div>
              </div>
            </div>
          ) : isEditMode && composer.loadPostError ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center space-y-4 max-w-md">
                <div className="text-red-500 text-4xl">
                  <Icon name={ICONS.CAUTION} size="lg" color="text-red-500" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-gray-900">{t('failed_to_load_post', { defaultValue: 'Failed to load post' })}</h3>
                  <p className="text-sm text-gray-600">
                    {composer.loadPostError instanceof Error
                      ? composer.loadPostError.message
                      : t('error_loading_post', { defaultValue: 'Error loading post data' })}
                  </p>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    {t('close', { defaultValue: 'Close' })}
                  </button>
                  <button
                    type="button"
                    onClick={composer.retryLoadPost}
                    disabled={composer.isLoadPostRefetching}
                    className={cn(
                      'px-4 py-2 flex items-center justify-center gap-1.5 rounded-md',
                      'bg-primary-400 text-primary-400-contrast font-semibold text-sm',
                      'border border-primary-400',
                      'hover:bg-primary-600 hover:border-primary-600',
                      'transition-colors duration-200',
                      'disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed'
                    )}
                  >
                    {composer.isLoadPostRefetching ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {t('retrying', { defaultValue: 'Retrying…' })}
                      </>
                    ) : (
                      t('retry', { defaultValue: 'Retry' })
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* ── Two-column body (lg+) / single-column (mobile) ── */}
              {/*
               * lg+: flex-row — left col (55%) has caption + toolbar,
               *      right col (45%, only when toggles active) has all sections.
               * <lg: flex-col — caption+toolbar fixed, sections scroll below (unchanged).
               *
               * The entire area is flex-1 min-h-0 so it fills the space between header
               * and footer without growing the modal taller than max-h.
               */}
              <div
                ref={resizeContainerRef}
                className={cn(
                  'flex-1 min-h-0 flex',
                  // Mobile: column; the body scrolls so the footer stays on screen with the keyboard open
                  'flex-col max-lg:overflow-y-auto',
                  // Desktop: row only when right-panel toggles are active (otherwise single-col)
                  hasRightPanelToggles && 'lg:flex-row'
                )}
              >

                {/* ── LEFT COLUMN: Caption + Toolbar ── */}
                <div
                  style={hasRightPanelToggles ? { width: `${resizeLeftPercent}%` } : undefined}
                  className={cn(
                    `px-6 sm:px-[33px] pt-4 max-lg:!w-auto ${hasRightPanelToggles ? 'lg:pr-4' : ''}`,
                    hasRightPanelToggles || activeToggles.size > 0
                      // Toggles open: scrollable on mobile, flex-col on desktop
                      ? 'flex-1 min-h-0 overflow-y-auto lg:overflow-visible lg:flex lg:flex-col lg:flex-none lg:min-w-0 lg:pb-4 scrollbar-none'
                      // No toggles: simple fit content
                      : 'flex-shrink-0 lg:w-full pb-4'
                  )}
                >
                  {/* Read-only reason: frozen content stays visible, never blank */}
                  {isContentLocked && <ReleaseLockBanner reason={lockReason} className="mb-4" />}

                  {/* Unconfirmed save (timeout / dropped connection): honest status instead of a raw error */}
                  {composer.saveUiState === 'still_saving' && (
                    <div
                      className="flex items-center gap-2 p-4 mb-4 bg-primary-50 border border-primary-200 rounded-lg"
                      role="status"
                      aria-live="polite"
                      data-testid="composer-still-saving"
                    >
                      <svg className="animate-spin h-4 w-4 text-primary-700 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <p className="text-sm font-medium text-primary-800">{t('saving_still_saving')}</p>
                    </div>
                  )}
                  {composer.saveUiState === 'still_saving_keep_checking' && (
                    <div
                      className="flex flex-wrap items-center gap-3 p-4 mb-4 bg-primary-50 border border-primary-200 rounded-lg"
                      role="status"
                      aria-live="polite"
                      data-testid="composer-still-saving-keep-checking"
                    >
                      <p className="text-sm font-medium text-primary-800 flex-1 min-w-[12rem]">{t('saving_still_saving_keep_checking')}</p>
                      <button
                        type="button"
                        onClick={() => { void composer.checkSaveAgain(); }}
                        disabled={composer.isCheckingAgain}
                        className="h-9 px-3 rounded-md border border-primary-300 bg-white text-sm font-semibold text-primary-800 hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t('saving_check_again')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowDiscardConfirm(true)}
                        disabled={composer.isCheckingAgain}
                        className="h-9 px-3 rounded-md border border-gray-300 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t('saving_discard_start_over')}
                      </button>
                    </div>
                  )}
                  {composer.saveUiState === 'not_saved' && (
                    <div
                      className="flex items-start gap-2 p-4 mb-4 bg-amber-50 border border-amber-200 rounded-lg"
                      role="alert"
                      data-testid="composer-not-saved"
                    >
                      <Icon
                        name={ICONS.CAUTION}
                        size="sm"
                        color="text-amber-700"
                        className="flex-shrink-0 mt-0.5"
                      />
                      <p className="text-sm font-medium text-amber-800">{t('saving_not_saved_try_again')}</p>
                    </div>
                  )}

                  {/* Error Display */}
                  {composer.isError && (
                    <div className="flex items-start gap-2 p-4 mb-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
                      <Icon
                        name={ICONS.CAUTION}
                        size="sm"
                        color="text-red-600"
                        className="flex-shrink-0 mt-0.5"
                      />
                      <div>
                        <p className="text-sm font-medium text-red-600">{t('error', { defaultValue: 'Error' })}</p>
                        <p className="text-sm text-red-600">
                          {/* Never the raw axios text ("timeout of 10000ms exceeded") */}
                          {composer.getSaveErrorMessage(composer.error, t('failed_save_post', { defaultValue: 'Failed to save post' }))}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Icon Toolbar — above caption */}
                  <div className="flex-shrink-0 mb-3">
                    <CreatorToolbar
                      activeToggles={activeToggles}
                      onToggle={handleToggle}
                      disabled={isBusy}
                      showCarousel={composer.media.length > 1}
                      hiddenToggles={hiddenToggles}
                    />
                  </div>

                  {/* Caption Editor — grows tall in two-column mode (lg+) */}
                  <PostEditor
                    value={composer.caption}
                    onChange={composer.setCaption}
                    platforms={selectedPlatforms()}
                    disabled={isBusy || isContentLocked}
                    compact={!hasRightPanelToggles}
                    fillHeight={hasRightPanelToggles}
                    className={cn(
                      hasRightPanelToggles
                        // Desktop: fill the left column
                        ? 'lg:flex-shrink lg:flex-1 lg:min-h-0 lg:[&_[contenteditable]]:!h-full'
                        : ''
                    )}
                    placeholder={t('whats_on_your_mind', { defaultValue: "What's on your mind?" })}
                    selectedChannels={composer.selectedChannels}
                    onInsertImage={handleInsertImage}
                  />

                  {/* Hashtags & Labels — below caption on desktop only */}
                  {isDesktop && (activeToggles.has('hashtags') || activeToggles.has('labels')) && (
                    <div className="hidden lg:block mt-3 space-y-3">
                      {activeToggles.has('hashtags') && (
                        <CreatorSectionCard id="creator-section-hashtags-left" icon="tag" title={t('hashtags', { defaultValue: 'Hashtags' })}>
                          <HashtagSection caption={composer.caption} onCaptionChange={composer.setCaption} readOnly={isBusy || isContentLocked} />
                        </CreatorSectionCard>
                      )}
                      {activeToggles.has('labels') && (
                        <CreatorSectionCard id="creator-section-labels-left" icon="label" title={t('organize', { defaultValue: 'Organize' })}>
                          <LabelsSection labels={composer.labels} onChange={composer.setLabels} disabled={isBusy || isContentLocked} />
                        </CreatorSectionCard>
                      )}
                    </div>
                  )}

                  {/* Mobile: all toggled sections in correct order (scrolls with left column) */}
                  {!isDesktop && activeToggles.size > 0 && (
                    <div className="lg:hidden mt-4 space-y-3 pb-4">
                      {/* 1. Media */}
                      {activeToggles.has('media') && (
                        <CreatorSectionCard id="creator-section-media-mobile" icon="photo_camera" title={t('media', { defaultValue: 'Media' })}>
                          <MediaSection
                            media={composer.media}
                            onAddMedia={composer.addMedia}
                            onRemoveMedia={composer.removeMedia}
                            onReorderMedia={composer.reorderMedia}
                            onSetMedia={composer.setMedia}
                            onSetChangedThumbnail={composer.setChangedThumbnail}
                            disabled={isBusy || isContentLocked}
                            showCarouselMode={activeToggles.has('carousel') || composer.isCarouselMode}
                            selectedPlatforms={selectedPlatforms()}
                          />
                          {showMediaTip && !isEditMode && !isShootingMode && (
                            <div className="relative mt-3 pl-8 pr-6 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-[13px] leading-[18px]">
                              <Icon name={ICONS.INFO} size="custom" color="text-amber-600" className="absolute left-2.5 top-2.5 text-[14px]" />
                              <span className="text-amber-700 font-semibold">{t('pssst', { defaultValue: 'Pssst!' })}</span>{' '}
                              <span className="text-amber-700">{t('media_tip_carousel', { defaultValue: 'By default, uploading multiple media creates multiple posts. Enable "Carousel" mode to post them together as a swipeable gallery.' })}</span>
                              <button type="button" onClick={handleDismissMediaTip} className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded-full transition-colors duration-200 focus:outline-none" aria-label={t('dismiss_tip', { defaultValue: 'Dismiss tip' })}>
                                <span className="text-[10px] leading-none">&#x2715;</span>
                              </button>
                            </div>
                          )}
                        </CreatorSectionCard>
                      )}
                      {/* 2. AI */}
                      {activeToggles.has('ai') && (
                        <CreatorSectionCard id="creator-section-ai-mobile" icon="auto_awesome" title={t('ai_assistant', { defaultValue: 'AI Assistant' })}>
                          <AISection caption={composer.caption} onCaptionChange={composer.setCaption} selectedChannels={composer.selectedChannels} disabled={isBusy} />
                        </CreatorSectionCard>
                      )}
                      {/* 3. Hashtags */}
                      {activeToggles.has('hashtags') && (
                        <CreatorSectionCard id="creator-section-hashtags-mobile" icon="tag" title={t('hashtags', { defaultValue: 'Hashtags' })}>
                          <HashtagSection caption={composer.caption} onCaptionChange={composer.setCaption} readOnly={isBusy || isContentLocked} />
                        </CreatorSectionCard>
                      )}
                      {/* 4. Labels */}
                      {activeToggles.has('labels') && (
                        <CreatorSectionCard id="creator-section-labels-mobile" icon="label" title={t('organize', { defaultValue: 'Organize' })}>
                          <LabelsSection labels={composer.labels} onChange={composer.setLabels} disabled={isBusy || isContentLocked} />
                        </CreatorSectionCard>
                      )}
                      {scheduleVisible && (
                        <>
                          {/* Channels Section */}
                          <CreatorSectionCard id="creator-section-channels-mobile" icon="rocket_launch" title={t('platforms', { defaultValue: 'Channels' })}>
                            <PlatformsSection
                              selectedChannels={composer.selectedChannels}
                              onSelectionChange={composer.setChannels}
                              disabled={isBusy}
                              mode="shoot_now"
                              mediaTypes={currentMediaTypesMemo}
                              channelOptions={composer.channelOptions}
                              onOptionsChange={updateChannelOptions}
                              hasVideo={hasVideo}
                              hasMedia={composer.media.length > 0}
                              caption={composer.caption}
                              media={composer.media}
                              isCarouselMode={composer.isCarouselMode}
                              channels={channelPreviewData}
                            />
                          </CreatorSectionCard>
                          {/* Schedule Section */}
                          <CreatorSectionCard id="creator-section-schedule-mobile" icon="schedule" title={t('schedule', { defaultValue: 'Schedule' })}>
                            <div className="space-y-4">
                              {showNowLaterPill && (
                                <div className="bg-gray-100 rounded-md p-0.5 flex">
                                  <button type="button" onClick={() => setScheduleMode('shoot_now')} className={cn('flex-1 flex items-center justify-center gap-1 py-1 px-2 text-xs font-medium rounded transition-all', scheduleMode === 'shoot_now' ? 'bg-gray-900 text-white shadow-sm' : 'bg-transparent text-gray-500 hover:text-gray-700')}>
                                    {/* eslint-disable-next-line i18next/no-literal-string */}
                                    <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>rocket_launch</span>
                                    {t('now', { defaultValue: 'Now' })}
                                  </button>
                                  <button type="button" onClick={() => setScheduleMode('schedule')} className={cn('flex-1 flex items-center justify-center gap-1 py-1 px-2 text-xs font-medium rounded transition-all', scheduleMode === 'schedule' ? 'bg-gray-900 text-white shadow-sm' : 'bg-transparent text-gray-500 hover:text-gray-700')}>
                                    {/* eslint-disable-next-line i18next/no-literal-string */}
                                    <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>schedule</span>
                                    {t('later', { defaultValue: 'Later' })}
                                  </button>
                                </div>
                              )}
                              {!isShootingMode && (
                                <>
                                  <CustomSelect label={t('select_calendar', { defaultValue: 'Calendar' })} placeholder={t('select_calendar_placeholder', { defaultValue: 'Select calendar' })} options={calendarOptions} value={selectedCalendar} onChange={handleCalendarChange} disabled={isBusy || isLoadingCalendars} compact />
                                  {selectedCalendar && eventOptions.length > 0 && (
                                    <CustomSelect label={t('add_to_campaign', { defaultValue: 'Campaign' })} placeholder={t('none', { defaultValue: 'None' })} options={eventOptions} value={selectedEvent} onChange={setSelectedEvent} disabled={isBusy || isLoadingEvents} helperText={t('campaign_helper', { defaultValue: 'Optionally link post to a campaign or holiday' })} compact />
                                  )}
                                </>
                              )}
                              {scheduleMode === 'schedule' && (
                                <ScheduleSection scheduledTime={composer.scheduledTime} onScheduleChange={composer.setScheduledTime} disabled={isBusy} compact />
                              )}
                            </div>
                          </CreatorSectionCard>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* ── RESIZE HANDLE (lg+ only) ── */}
                {isDesktop && hasRightPanelToggles && (
                  <div
                    className="hidden lg:flex items-stretch cursor-col-resize group select-none flex-shrink-0"
                    {...resizeHandleProps}
                  >
                    <div className="w-1 mx-0.5 rounded-full bg-gray-200 group-hover:bg-primary-400 group-active:bg-primary-500 transition-colors my-4" />
                  </div>
                )}

                {/* ── RIGHT COLUMN: Toggled sections (lg+ only, only when right-panel toggles active) ── */}
                {isDesktop && hasRightPanelToggles && (
                  <div
                    style={hasRightPanelToggles ? { width: `${100 - resizeLeftPercent}%` } : undefined}
                    className="hidden lg:flex lg:flex-col min-h-0 min-w-0 flex-1 max-lg:!w-auto"
                  >
                    {/* Top scroll fade (desktop right panel) — outside scroll container, overlaps */}
                    <div className={cn(
                      'flex-shrink-0 h-6 -mb-6 relative z-10 pointer-events-none bg-gradient-to-b from-white to-transparent transition-opacity duration-200',
                      canScrollUp ? 'opacity-100' : 'opacity-0'
                    )} />
                    <div
                      ref={scrollRef}
                      className="flex-1 overflow-y-auto pl-4 pr-6 sm:pr-[33px] pt-4 pb-5"
                    >

                      {/* Sections (desktop right panel) */}
                      {activeToggles.has('media') && (
                        <CreatorSectionCard id="creator-section-media" icon="photo_camera" title={t('media', { defaultValue: 'Media' })}>
                          <MediaSection
                            media={composer.media}
                            onAddMedia={composer.addMedia}
                            onRemoveMedia={composer.removeMedia}
                            onReorderMedia={composer.reorderMedia}
                            onSetMedia={composer.setMedia}
                            onSetChangedThumbnail={composer.setChangedThumbnail}
                            disabled={isBusy || isContentLocked}
                            showCarouselMode={activeToggles.has('carousel') || composer.isCarouselMode}
                            selectedPlatforms={selectedPlatforms()}
                          />
                          {showMediaTip && !isEditMode && !isShootingMode && (
                            <div className="relative mt-3 pl-8 pr-6 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-[13px] leading-[18px]">
                              <Icon name={ICONS.INFO} size="custom" color="text-amber-600" className="absolute left-2.5 top-2.5 text-[14px]" />
                              <span className="text-amber-700 font-semibold">{t('pssst', { defaultValue: 'Pssst!' })}</span>{' '}
                              <span className="text-amber-700">{t('media_tip_carousel', { defaultValue: 'By default, uploading multiple media creates multiple posts. Enable "Carousel" mode to post them together as a swipeable gallery.' })}</span>
                              <button type="button" onClick={handleDismissMediaTip} className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded-full transition-colors duration-200 focus:outline-none" aria-label={t('dismiss_tip', { defaultValue: 'Dismiss tip' })}>
                                <span className="text-[10px] leading-none">&#x2715;</span>
                              </button>
                            </div>
                          )}
                        </CreatorSectionCard>
                      )}

                      {activeToggles.has('ai') && (
                        <CreatorSectionCard id="creator-section-ai" icon="auto_awesome" title={t('ai_assistant', { defaultValue: 'AI Assistant' })}>
                          <AISection caption={composer.caption} onCaptionChange={composer.setCaption} selectedChannels={composer.selectedChannels} disabled={isBusy} />
                        </CreatorSectionCard>
                      )}

                      {scheduleVisible && (
                        <>
                          {/* Channels Section */}
                          <CreatorSectionCard id="creator-section-channels" icon="rocket_launch" title={t('platforms', { defaultValue: 'Channels' })}>
                            <PlatformsSection
                              selectedChannels={composer.selectedChannels}
                              onSelectionChange={composer.setChannels}
                              disabled={isBusy}
                              mode="shoot_now"
                              mediaTypes={currentMediaTypesMemo}
                              channelOptions={composer.channelOptions}
                              onOptionsChange={updateChannelOptions}
                              hasVideo={hasVideo}
                              hasMedia={composer.media.length > 0}
                              caption={composer.caption}
                              media={composer.media}
                              isCarouselMode={composer.isCarouselMode}
                              channels={channelPreviewData}
                            />
                          </CreatorSectionCard>

                          {/* Schedule Section */}
                          <CreatorSectionCard id="creator-section-schedule" icon="schedule" title={t('schedule', { defaultValue: 'Schedule' })}>
                            <div className="space-y-4">
                              {/* Now / Later pill toggle */}
                              {showNowLaterPill && (
                                <div className="bg-gray-100 rounded-md p-0.5 flex">
                                  <button
                                    type="button"
                                    onClick={() => setScheduleMode('shoot_now')}
                                    className={cn(
                                      'flex-1 flex items-center justify-center gap-1 py-1 px-2 text-xs font-medium rounded transition-all',
                                      scheduleMode === 'shoot_now'
                                        ? 'bg-gray-900 text-white shadow-sm'
                                        : 'bg-transparent text-gray-500 hover:text-gray-700'
                                    )}
                                  >
                                    {/* eslint-disable-next-line i18next/no-literal-string */}
                                    <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>rocket_launch</span>
                                    {t('now', { defaultValue: 'Now' })}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setScheduleMode('schedule')}
                                    className={cn(
                                      'flex-1 flex items-center justify-center gap-1 py-1 px-2 text-xs font-medium rounded transition-all',
                                      scheduleMode === 'schedule'
                                        ? 'bg-gray-900 text-white shadow-sm'
                                        : 'bg-transparent text-gray-500 hover:text-gray-700'
                                    )}
                                  >
                                    {/* eslint-disable-next-line i18next/no-literal-string */}
                                    <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>schedule</span>
                                    {t('later', { defaultValue: 'Later' })}
                                  </button>
                                </div>
                              )}

                              {/* Calendar & Campaign - Not in shooting mode */}
                              {!isShootingMode && (
                                <>
                                  <CustomSelect
                                    label={t('select_calendar', { defaultValue: 'Calendar' })}
                                    placeholder={t('select_calendar_placeholder', { defaultValue: 'Select calendar' })}
                                    options={calendarOptions}
                                    value={selectedCalendar}
                                    onChange={handleCalendarChange}
                                    disabled={isBusy || isLoadingCalendars}
                                    compact
                                  />
                                  {selectedCalendar && eventOptions.length > 0 && (
                                    <CustomSelect
                                      label={t('add_to_campaign', { defaultValue: 'Campaign' })}
                                      placeholder={t('none', { defaultValue: 'None' })}
                                      options={eventOptions}
                                      value={selectedEvent}
                                      onChange={setSelectedEvent}
                                      disabled={isBusy || isLoadingEvents}
                                      helperText={t('campaign_helper', { defaultValue: 'Optionally link post to a campaign or holiday' })}
                                      compact
                                    />
                                  )}
                                </>
                              )}

                              {/* Schedule Section (only if Later mode) */}
                              {scheduleMode === 'schedule' && (
                                <ScheduleSection
                                  scheduledTime={composer.scheduledTime}
                                  onScheduleChange={composer.setScheduledTime}
                                  disabled={isBusy}
                                  compact
                                />
                              )}
                            </div>
                          </CreatorSectionCard>
                        </>
                      )}

                    </div>
                    {/* Bottom scroll fade (desktop right panel) — outside scroll container, overlaps */}
                    <div className={cn(
                      'flex-shrink-0 h-6 -mt-6 relative z-10 pointer-events-none bg-gradient-to-t from-white to-transparent transition-opacity duration-200',
                      canScrollDown ? 'opacity-100' : 'opacity-0'
                    )} />
                  </div>
                )}
              </div>

              {/* A disabled button cannot expose its tooltip on touch screens. */}
              {scheduleVisible && disabledReason && !releasePerms.isLoading && !isBusy && (
                <div className="lg:hidden flex-shrink-0 px-6 sm:px-[33px] pt-2 text-xs text-gray-700" role="status">
                  <span>{disabledReason}</span>
                  {disabledReasonIsSetup && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="font-semibold text-primary-700 underline underline-offset-2"
                        onClick={() => {
                          const sectionId = composer.selectedChannels.length === 0
                            ? 'creator-section-channels-mobile'
                            : 'creator-section-schedule-mobile';
                          document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                      >
                        {t('schedule_requirements_open', { defaultValue: 'Go to scheduling options' })}
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Footer */}
              <ModalFooter noBorder justify="center-lg-end" className="flex-shrink-0 !px-6 sm:!px-[33px] !pt-4 !pb-5">
                {/* Cancel - icon-only square button */}
                <CancelButton onClick={handleClose} disabled={isBusy && composer.saveUiState !== 'still_saving_keep_checking'} tooltip={t('cancel', { defaultValue: 'Cancel' })} />

                {/* Primary CTA - wide teal button with optional dropdown */}
                <Tooltip content={disabledReason || ''} side="top" disabled={!disabledReason}>
                <div className="relative">
                  {ctaConfig.showDropdown ? (
                    // Split button with dropdown for DRAFT posts
                    <div className="flex">
                      <button
                        type="button"
                        onClick={handlePrimaryAction}
                        disabled={isDisabled}
                        className={cn(
                          'w-[9rem] sm:w-[11rem] h-[44px] sm:h-10 flex items-center justify-center gap-1.5 sm:gap-2 rounded-l-md',
                          'bg-primary-400 text-primary-400-contrast font-semibold text-xs sm:text-sm',
                          'border border-primary-400',
                          'hover:bg-primary-600 hover:border-primary-600',
                          'transition-colors duration-200',
                          'disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed'
                        )}
                      >
                        {isBusy ? (
                          <svg className="animate-spin h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        ) : (
                          <>
                            <span className="material-symbols-outlined leading-none" style={{ fontSize: 'clamp(14px, 2vw, 18px)' }}>{ctaConfig.materialIcon}</span>
                            {ctaConfig.label}
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCtaDropdown(!showCtaDropdown)}
                        disabled={menuOnly ? isBusy || releasePerms.isLoading : isDisabled}
                        className={cn(
                          'w-[44px] sm:w-10 h-[44px] sm:h-10 flex items-center justify-center rounded-r-md border-l',
                          'bg-primary-400 text-primary-400-contrast',
                          'border-primary-600',
                          'hover:bg-primary-600',
                          'transition-colors duration-200',
                          'disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed'
                        )}
                      >
                        {/* eslint-disable-next-line i18next/no-literal-string */}
                        <span className="material-symbols-outlined text-[16px]">expand_more</span>
                      </button>
                    </div>
                  ) : (
                    // Single button for READY posts or when rocket is ON
                    <button
                      type="button"
                      onClick={handlePrimaryAction}
                      disabled={isDisabled}
                      className={cn(
                        'w-[10rem] sm:w-[13rem] h-[44px] sm:h-10 flex items-center justify-center gap-1.5 sm:gap-2 rounded-md',
                        'bg-primary-400 text-primary-400-contrast font-semibold text-xs sm:text-sm',
                        'border border-primary-400',
                        'hover:bg-primary-600 hover:border-primary-600',
                        'transition-colors duration-200',
                        'disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed'
                      )}
                    >
                      {isBusy ? (
                        <svg className="animate-spin h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : (
                        <>
                          <span className="material-symbols-outlined leading-none" style={{ fontSize: 'clamp(14px, 2vw, 18px)' }}>{ctaConfig.materialIcon}</span>
                          {ctaConfig.label}
                        </>
                      )}
                    </button>
                  )}

                  {/* Dropdown menu for "Save & Mark Ready" */}
                  {showCtaDropdown && ctaConfig.showDropdown && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowCtaDropdown(false)}
                      />
                      <div className="absolute right-0 bottom-full mb-2 w-60 sm:w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1">
                        {!permissions.workflowEnabled && (
                          <button
                            onClick={() => {
                              setShowCtaDropdown(false);
                              composer.handleSaveAndMarkReady();
                            }}
                            disabled={isDisabled}
                            className="w-full min-h-[44px] flex items-center gap-2 px-3 sm:px-4 py-3 sm:py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {/* eslint-disable-next-line i18next/no-literal-string */}
                            <span className="material-symbols-outlined leading-none" style={{ fontSize: 'clamp(14px, 2vw, 18px)' }}>check_circle</span>
                            {t('save_and_mark_ready', { defaultValue: 'Save & Mark Ready' })}
                          </button>
                        )}
                        {showReleaseDirect && (
                          <button
                            type="button"
                            data-testid="composer-save-and-release"
                            onClick={() => {
                              setShowCtaDropdown(false);
                              void runSaveAction(composer.handleSaveAndRelease);
                            }}
                            disabled={isBusy || releasePerms.isLoading || !composer.canSaveDraft}
                            className="w-full min-h-[44px] flex items-center gap-2 px-3 sm:px-4 py-3 sm:py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Icon name={ICONS.TICK_CIRCLE} size="sm" ariaHidden />
                            {permsPostId === null ? t('composer_create_and_release') : t('composer_save_and_release')}
                          </button>
                        )}
                        {showSaveAndSendForReview && (
                          <button
                            type="button"
                            data-testid="composer-send-for-review"
                            onClick={() => {
                              setShowCtaDropdown(false);
                              void runSaveAction(composer.handleSaveAndSendForReview);
                            }}
                            disabled={isBusy || releasePerms.isLoading || !composer.canSaveDraft}
                            className="w-full min-h-[44px] flex items-center gap-2 px-3 sm:px-4 py-3 sm:py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Icon name={ICONS.PAPER_PLANE} size="sm" ariaHidden />
                            {t('composer_save_and_send_for_review')}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
                </Tooltip>
              </ModalFooter>
            </>
          )}
        </Modal>
      </TooltipProvider>

      {/* Discard Confirmation Dialog */}
      <ConfirmDialog
        open={showDiscardConfirm}
        onOpenChange={setShowDiscardConfirm}
        title={t('discard_changes', { defaultValue: 'Discard Changes?' })}
        description={
          composer.saveUiState === 'still_saving_keep_checking'
            ? t('saving_discard_may_exist')
            : t('unsaved_changes_lost', { defaultValue: 'Your unsaved changes will be lost.' })
        }
        variant="danger"
        confirmText={t('discard', { defaultValue: 'Discard' })}
        cancelText={t('continue', { defaultValue: 'Continue Editing' })}
        onConfirm={handleConfirmDiscard}
        onCancel={() => setShowDiscardConfirm(false)}
        closeOnConfirm={false}
      />

      {/* Unsaved Draft Restore Prompt */}
      <ConfirmDialogSimple
        open={showRestorePrompt}
        onOpenChange={setShowRestorePrompt}
        title={t('draft_restore_title', { defaultValue: 'Unsaved Draft Found' })}
        description={
          pendingRestorableDraft?.savedAt
            ? t('draft_restore_description', {
                defaultValue: 'You have an unsaved draft from {{time}}.',
                time: formatDateTime(new Date(pendingRestorableDraft.savedAt)),
              })
            : t('draft_restore_description_generic', { defaultValue: 'You have an unsaved draft.' })
        }
        variant="default"
        confirmText={t('restore_draft', { defaultValue: 'Restore' })}
        cancelText={t('start_fresh', { defaultValue: 'Start Fresh' })}
        onConfirm={handleRestoreDraft}
        onCancel={handleStartFreshDraft}
      />
    </>
  );
};
